import * as React from 'react'
import { INode } from 'shared/types/tree'
import { Box, Button, TextareaAutosize } from '@mui/material'
import { WithTree } from '../navigationHelpers/WithTree'
import { PotentiallyHtml } from '../../PotentiallyHtml'
import { doInBatches } from 'shared/utils'
import imgOnerrorData from './imgOnError'
import { openNode } from '../../utility/TreeNavigation'
import { createIdMap, NodeIdMap } from '../../version_mapping/utility'
import {
  contentFromNode,
  nodeForNewContentString,
  updateQuestionDescription,
} from './utility'
import { NodeContentUpdateContext } from './nodeContentUpdateContext'

function formatHtml(html: string) {
  const tab = '\t'
  let result = ''
  let indent = ''

  html.split(/>\s*</).forEach(function (element) {
    if (element.match(/^\/\w/)) {
      indent = indent.substring(tab.length)
    }

    result += indent + '<' + element + '>\r\n'

    if (element.match(/^<?\w[^>]*[^/]$/) && !element.startsWith('input')) {
      indent += tab
    }
  })
  return result.substring(1, result.length - 3)
}

export function HtmlContentUpdatesWrapper() {
  return <WithTree Wrapped={WithContext} />
}

function WithContext(params: { tree: INode; reFetchTree: () => void }) {
  // Trying to make it easy/convenient to add more change operations in the future
  // If we want to do other kinds of content updates we need to create and use
  // different change functions and replace here, maybe based on routing or UI selection?
  const nodeUpdateFunctions = React.useMemo(() => imgOnerrorData, [])

  const contextParams = React.useMemo(() => {
    const updateNodes = (...nodes: INode[]) => {
      const nodeUpdateFunc = async (node: INode) => {
        const currentContent = contentFromNode(node)
        const newContent = nodeUpdateFunctions.convertContent(currentContent)
        if (!newContent) return Promise.resolve()
        const newNode = nodeForNewContentString(newContent, node)
        await updateQuestionDescription(newNode)
      }
      return doInBatches(nodes, nodeUpdateFunc).then(params.reFetchTree)
    }
    return {
      ...nodeUpdateFunctions,
      updateNodes,
    }
  }, [params.reFetchTree, nodeUpdateFunctions])

  return (
    <NodeContentUpdateContext.Provider value={contextParams}>
      <HtmlContentUpdates {...params} />
    </NodeContentUpdateContext.Provider>
  )
}

export function HtmlContentUpdates({ tree }: { tree: INode }) {
  const [idMap, setIdMap] = React.useState<NodeIdMap>({})
  const [nodes, setNodes] = React.useState<INode[]>([])
  const [selectedNode, setSelectedNode] = React.useState<INode | undefined>()

  const { findAffectedNodes, updateNodes } = React.useContext(
    NodeContentUpdateContext,
  )

  React.useEffect(() => {
    const nodes = findAffectedNodes(tree)
    setNodes(nodes)
    setIdMap(createIdMap(tree))
  }, [tree, findAffectedNodes])

  function updateAll() {
    updateNodes(...nodes)
  }

  function nodeUpdateFunc(node: INode) {
    return () => {
      updateNodes(node).then(() => setSelectedNode(undefined))
    }
  }

  return (
    <Box>
      <Box>
        Nodes: {nodes.length} <Button onClick={updateAll}>Update All</Button>
      </Box>
      {selectedNode && (
        <Box padding={'10px'}>
          <NodeView
            node={selectedNode}
            idMap={idMap}
            updateFunc={nodeUpdateFunc(selectedNode)}
          />
        </Box>
      )}
      <Box padding={'10px'}>
        <NodeList nodes={nodes} onSelect={setSelectedNode} />
      </Box>
    </Box>
  )
}

function NodeView({
  node,
  idMap,
  updateFunc,
}: {
  node: INode
  idMap: NodeIdMap
  updateFunc: () => void
}) {
  const [htmlString, setHtmlString] = React.useState('')
  const [converted, setConverted] = React.useState<string>('')

  const { convertContent } = React.useContext(NodeContentUpdateContext)

  React.useEffect(() => {
    const html = contentFromNode(node)
    setHtmlString(html)
    const converted = convertContent(html) || ''
    setConverted(converted)
  }, [node, convertContent])
  return (
    <Box>
      <Box>
        <Button onClick={() => openNode(node, idMap)}>{node.title}</Button>
      </Box>
      <Button disabled={!converted} onClick={updateFunc}>
        Update
      </Button>
      <HtmlDisplay html={htmlString} />
      <HtmlDisplay html={converted || ''} />
    </Box>
  )
}

function HtmlDisplay({ html }: { html: string }) {
  const [asString, setAsString] = React.useState(false)
  return (
    <Box>
      <Box>
        <Button onClick={() => setAsString(!asString)}>
          {asString ? '<<' : '>>'}
        </Button>
      </Box>
      <Box
        padding={'5px'}
        border={'solid 1px black'}
        maxHeight={'260px'}
        height={'260px'}
        style={{ backgroundColor: 'white' }}
        overflow={'auto'}
      >
        {asString && (
          <TextareaAutosize
            style={{ width: '100%', maxHeight: '100%', overflow: 'auto' }}
            value={formatHtml(html)}
          />
        )}
        {!asString && <PotentiallyHtml s={html} />}
      </Box>
    </Box>
  )
}

function NodeList({
  nodes,
  onSelect,
}: {
  nodes: INode[]
  onSelect: (node: INode) => void
}) {
  return (
    <Box>
      {nodes.map((node) => {
        return (
          <Box key={node.id}>
            <Button onClick={() => onSelect(node)}>{node.title}</Button>
          </Box>
        )
      })}
    </Box>
  )
}
